import './HistoricalPercentageChangeChart.scss';
import React, { useCallback, useContext, useMemo } from 'react';
import { computeYDomain, useHistoricalVolumeChartSeries } from './series';
import { computeChartData } from './chartData.ts';

import {
    postfixNumber,
    timeFormatConstants,
} from '@instinet-analytics/core-lib';
import { axisRight } from 'd3fc';
import { BaseType, select, Selection } from 'd3-selection';
import { CartesianChart } from 'utils/components/CartesianChart/CartesianChart.tsx';
import { doNothing } from 'utils/general.ts';
import { useScale } from 'utils/components/TimeSeriesChart/scale';
import { DateTime } from 'luxon';
import AppContext from 'app/AppContext.tsx';
import { BarMouseOver } from './barMouseOver';
import { HistoricalPercentageChangeDataItem } from 'widgets/historicalPercentageChange/data/historicalPercentageChangeDataItem';
import classNames from 'classnames';
import { useWidgetContext } from 'widgets/WidgetContext.tsx';
import { ChartLabels } from './chartLabels.tsx';

const { date } = timeFormatConstants;

export function HistoricalPercentageChangeChart({
    barMouseOver,
    className,
    hideAverageVolume,
    hidePrice,
    hideVolume,
    hideYAxis,
    data,
    height,
    periodQty,
    pricePointSize,
    width,
}: {
    barMouseOver: BarMouseOver;
    className?: string;
    hideAverageVolume: boolean;
    hidePrice: boolean;
    hideVolume: boolean;
    hideYAxis?: boolean;
    data: HistoricalPercentageChangeDataItem[];
    pricePointSize?: number;
    periodQty: number;
    height: number;
    width: number;
}) {
    const { numberFormat } = useContext(AppContext);
    const { widgetKey } = useWidgetContext();

    const { averageVolume, chartData, volumeToPrice } = useMemo(
        () => computeChartData(data, periodQty),
        [data, periodQty]
    );

    const yScale = useScale(computeYDomain(chartData));
    const xScale = useScale([-0.5, chartData.length - 1 + 0.1]);
    const series = useHistoricalVolumeChartSeries(
        averageVolume,
        chartData,
        width,
        barMouseOver,
        pricePointSize
    );

    const yTickFormat = (volume: number) => {
        //return volume === 0 ? '' : postfixNumber(volume);
        const price = volumeToPrice(volume);
        if (isNaN(price)) {
            return '';
        }
        return numberFormat.n2(price);
    };

    const rightYAxis = axisRight(yScale)
        .ticks(5)
        .tickFormat((volume: number) => postfixNumber(volume));

    const decorate = (
        selection: Selection<BaseType, unknown, null, undefined>
    ) => {
        selection
            .enter()
            .append('d3fc-svg')
            // move the element into the right-axis cell
            .style('grid-column', 4)
            .style('grid-row', 3)
            // and give the axis a width
            .classed('y-axis-right', true)
            .style('width', '25px')
            // when there's a measure event (namespaced to avoid removing existing handlers)
            .on('measure.z-axis', (event) => {
                // set the range on the scale to the elements height
                yScale.range([event.detail.height, 0]);
            })
            .on('draw.z-axis', (event) => {
                // draw the axis into the svg within the d3fc-svg element
                select(event.currentTarget).select('svg').call(rightYAxis);
            });
    };

    const xTickFormat = useCallback(
        (d: number) => {
            return chartData[d]?.date === undefined
                ? ''
                : DateTime.fromMillis(chartData[d].date).toFormat(date);
        },
        [chartData]
    );

    const cssClasses = classNames(
        'HistoricalPercentageChangeChart',
        className,
        hidePrice && 'hide-price',
        hideVolume && 'hide-volume',
        hideYAxis && 'hide-y-axis',
        hideAverageVolume && 'hide-average-volume'
    );

    return (
        <>
            <CartesianChart
                id={`HistoricalPercentageChangeChart-${widgetKey}`}
                className={cssClasses}
                autoSizeYAxis={true}
                height={height}
                data={chartData}
                chartSeries={series}
                decorate={hideYAxis ? undefined : decorate}
                yScale={yScale}
                xScale={xScale}
                yTickFormat={yTickFormat}
                xTickFormat={xTickFormat}
                width={width}
                onPlotAreaDimensionsChanged={doNothing}
            />
            <ChartLabels
                height={height}
                width={width}
                yScale={yScale}
                numberFormat={numberFormat}
                averageVolume={averageVolume}
            />
        </>
    );
}
